import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { BackupApiService } from './backup.apiservice';
import { Backup, BackupSiteReply, StagingFromBackupReply } from './backup.model';
import { distinctUntilChanged, filter, map, share, tap } from 'rxjs/operators';

@Injectable()
export class BackupService {
  backups$$: BehaviorSubject<Backup[]> = new BehaviorSubject([]);
  stagingBackups$$: BehaviorSubject<Backup[]> = new BehaviorSubject([]);

  constructor(private api: BackupApiService) {}

  get backups(): Observable<Backup[]> {
    return this.backups$$.asObservable().pipe(
      filter((backups: Backup[]) => !!backups),
      distinctUntilChanged(
        (old: Backup[], current: Backup[]) =>
          old.length === current.length &&
          current.length > 0 &&
          old[0].dateTime === current[0].dateTime &&
          old[0].inProgress === current[0].inProgress,
      ),
      share(),
    );
  }

  get stagingBackups(): Observable<Backup[]> {
    return this.stagingBackups$$.asObservable().pipe(
      filter((backups: Backup[]) => !!backups),
      distinctUntilChanged(
        (old: Backup[], current: Backup[]) =>
          old.length === current.length &&
          current.length > 0 &&
          old[0].dateTime === current[0].dateTime &&
          old[0].inProgress === current[0].inProgress,
      ),
      share(),
    );
  }

  get(siteId: string): Observable<string[]> {
    return this.api.get(siteId);
  }

  restore(siteId: string, backupDateTime: string): Observable<string> {
    return this.api.restore(siteId, backupDateTime);
  }

  list(siteId: string, pageSize: number, offset: number, isStaging?: boolean): Observable<Backup[]> {
    return this.api.list(siteId, offset, pageSize, isStaging).pipe(
      map((backups: Backup[]) => backups.slice(0, pageSize)),
      tap((backups: Backup[]) => (isStaging ? this.stagingBackups$$.next(backups) : this.backups$$.next(backups))),
    );
  }

  lastBackup(siteId: string, isStaging?: boolean): Observable<Backup[]> {
    return this.api.list(siteId, 0, 1, isStaging);
  }

  numberOfBackups(siteId: string, isStaging?: boolean): Observable<number> {
    return this.api.getTotalNumber(siteId, isStaging);
  }

  create(siteId: string, description: string, isStaging?: boolean): Observable<BackupSiteReply> {
    return this.api.create(siteId, description, isStaging);
  }

  createStagingFromBackup(siteId: string, backupTimeStamp: string): Observable<StagingFromBackupReply> {
    const dateTimeString = this.convertToDateTimeString(backupTimeStamp);
    return this.api.createStagingFromBackup(siteId, dateTimeString);
  }

  // Portal normally takes care of this parsing,  but since we're communicating directly with the microservice
  // this has to be done here.
  convertToDateTimeString(backupTimeStamp: string): string {
    return backupTimeStamp.split('.')[0] + 'Z';
  }
}
